import { z } from 'zod'
import type { GameModeId } from '@/lib/legal/game-mode-terms'

export const ALL_LEAGUE_CODES = ['england', 'italy', 'spain', 'germany', 'france'] as const
export type LeagueCode = (typeof ALL_LEAGUE_CODES)[number]

export const LEAGUE_CODE_ENUM = z.enum(ALL_LEAGUE_CODES)

const LEAGUE_CODE_SET: ReadonlySet<string> = new Set(ALL_LEAGUE_CODES)

export function isLeagueCode(value: unknown): value is LeagueCode {
  return typeof value === 'string' && LEAGUE_CODE_SET.has(value)
}

/**
 * Maps the wizard's UI-level league codes ('england', 'italy', ...) to the
 * numeric `competition_id` values used by `player_rating_view` and the
 * upstream Opta data feed:
 *   england → '8'  (EPL — Premier League)
 *   italy   → '21' (Serie A)
 *   germany → '22' (Bundesliga)
 *   spain   → '23' (La Liga)
 *   france  → '24' (Ligue 1)
 *
 * Source of truth: `public.competitions` table on Supabase
 * (project ijkucgjwqkndhlvgrcad). If a new league is added to the wizard
 * (and to ALL_LEAGUE_CODES), it must be added here too — and the
 * `competitions` row must exist with the matching short_code.
 */
export const LEAGUE_CODE_TO_COMPETITION_ID: Readonly<Record<LeagueCode, string>> = {
  england: '8',
  italy: '21',
  germany: '22',
  spain: '23',
  france: '24'
} as const

/**
 * Translates a list of UI league codes into the `competition_id` strings
 * used by player-pool queries. Invalid codes are silently dropped (the
 * caller's responsibility to validate via isLeagueCode if strictness matters).
 * Output preserves input order and removes duplicates.
 */
export function competitionIdsForLeagueCodes(codes: readonly string[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const code of codes) {
    if (!isLeagueCode(code)) continue
    const competitionId = LEAGUE_CODE_TO_COMPETITION_ID[code]
    if (!seen.has(competitionId)) {
      seen.add(competitionId)
      out.push(competitionId)
    }
  }
  return out
}

export type LeagueValidationResult =
  | { ok: true; selectedLeagues: LeagueCode[] }
  | {
      ok: false
      code: 'cash_requires_all_five' | 'token_requires_at_least_one' | 'invalid_league_code'
    }

export function validateLeagueSelection(
  mode: GameModeId,
  selectedLeagues: readonly LeagueCode[] | readonly string[]
): LeagueValidationResult {
  const dedup = Array.from(new Set(selectedLeagues))
  for (const code of dedup) {
    if (!isLeagueCode(code)) {
      return { ok: false, code: 'invalid_league_code' }
    }
  }
  const typed = dedup as LeagueCode[]

  if (mode === 'cash') {
    if (typed.length !== ALL_LEAGUE_CODES.length) {
      return { ok: false, code: 'cash_requires_all_five' }
    }
    const set = new Set(typed)
    for (const required of ALL_LEAGUE_CODES) {
      if (!set.has(required)) {
        return { ok: false, code: 'cash_requires_all_five' }
      }
    }
    return { ok: true, selectedLeagues: [...ALL_LEAGUE_CODES] }
  }

  if (typed.length < 1) {
    return { ok: false, code: 'token_requires_at_least_one' }
  }
  return { ok: true, selectedLeagues: typed }
}
